import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../../components/AppIcon';
import { LoadingState } from '../../../../components/AsyncStates';
import { useMyWorkPoints } from '@/hooks/useMyWorkPoints';
import { useCurrency } from '@/hooks/useCurrency';
import { MatchRole } from '@/models/tournament';

const roleKeys: Record<MatchRole, string> = {
  [MatchRole.HeadReferee]: 'Head Referee',
  [MatchRole.AssistantReferee]: 'Assistant Referee',
  [MatchRole.FourthOfficial]: 'Fourth Official',
  [MatchRole.Commissioner]: 'Commissioner',
};

const WorkPointsTab: React.FC = () => {
  const { t, i18n } = useTranslation('tournament');

  // Default to current month
  const now = new Date();
  const defaultStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
  const defaultEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0];

  const [startDate, setStartDate] = useState(defaultStart);
  const [endDate, setEndDate] = useState(defaultEnd);

  const { data: summary, loading, error } = useMyWorkPoints({ startDate, endDate });
  const { format: formatCurrency } = useCurrency();

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString(i18n.language, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="py-8">
        <LoadingState title={t('Loading data...')} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-card border border-border rounded-lg p-12 text-center">
        <Icon name="AlertCircle" size={48} className="mx-auto text-red-400 mb-4" />
        <h3 className="text-lg font-semibold text-foreground mb-2">{t('Error loading work points')}</h3>
        <p className="text-muted-foreground">{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="work-points-tab">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-card border border-border rounded-lg p-5" data-testid="wp-card-total-points">
          <div className="flex items-center justify-between mb-2">
            <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
              <Icon name="Zap" size={20} className="text-blue-600" />
            </div>
          </div>
          <p className="text-2xl font-bold text-foreground">{summary?.totalWorkPoints ?? 0}</p>
          <p className="text-sm text-muted-foreground">{t('Total Work Points')}</p>
        </div>

        <div className="bg-card border border-border rounded-lg p-5" data-testid="wp-card-currency">
          <div className="flex items-center justify-between mb-2">
            <div className="w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center">
              <Icon name="DollarSign" size={20} className="text-green-600" />
            </div>
          </div>
          <p className="text-2xl font-bold text-foreground">{formatCurrency(summary?.totalCurrencyAmount ?? 0)}</p>
          <p className="text-sm text-muted-foreground">{t('Total Currency Amount')}</p>
        </div>

        <div className="bg-card border border-border rounded-lg p-5" data-testid="wp-card-match-count">
          <div className="flex items-center justify-between mb-2">
            <div className="w-10 h-10 bg-amber-100 dark:bg-amber-900/30 rounded-lg flex items-center justify-center">
              <Icon name="Trophy" size={20} className="text-amber-600" />
            </div>
          </div>
          <p className="text-2xl font-bold text-foreground">{summary?.matchCount ?? 0}</p>
          <p className="text-sm text-muted-foreground">{t('Match Count')}</p>
        </div>
      </div>

      {/* Date Range Filter */}
      <div className="bg-card border border-border rounded-lg p-4">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1">{t('Start Date')}</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="border border-input rounded-md px-3 py-2 text-sm bg-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary transition-colors"
              data-testid="wp-start-date"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1">{t('End Date')}</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="border border-input rounded-md px-3 py-2 text-sm bg-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary transition-colors"
              data-testid="wp-end-date"
            />
          </div>
        </div>
      </div>

      {/* Recent Matches List */}
      <div>
        <h3 className="text-sm font-semibold text-foreground mb-3">{t('Recent Matches')}</h3>
        {summary?.recentMatches && summary.recentMatches.length > 0 ? (
          <div className="space-y-2">
            {summary.recentMatches.map((match) => (
              <div
                key={match.matchId}
                className="bg-card border border-border rounded-lg p-4 hover:shadow-sm transition-shadow"
                data-testid={`wp-match-${match.matchId}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <p className="text-xs text-muted-foreground mb-1">{match.tournamentName}</p>
                    <p className="text-sm font-semibold text-foreground">
                      {match.team1Name} <span className="text-muted-foreground">{t('vs')}</span> {match.team2Name}
                    </p>
                    <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                      <span className="flex items-center">
                        <Icon name="Calendar" size={12} className="me-1 text-gray-400" />
                        {formatDate(match.scheduledDateTime)}
                      </span>
                      <span className="flex items-center">
                        <Icon name="MapPin" size={12} className="me-1 text-gray-400" />
                        {match.location}
                      </span>
                      <span className="flex items-center">
                        <Icon name="UserCog" size={12} className="me-1 text-primary" />
                        <span className="font-medium text-primary">{t(roleKeys[match.role] ?? 'Unknown')}</span>
                      </span>
                    </div>
                  </div>
                  {match.workPoints > 0 && (
                    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300">
                      <Icon name="Zap" size={12} className="me-1" />
                      {match.workPoints} {t('pts')}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-card border border-border rounded-lg p-12 text-center" data-testid="empty-work-points">
            <Icon name="Calculator" size={48} className="mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-semibold text-foreground mb-2">{t('No work points data')}</h3>
            <p className="text-muted-foreground">{t('No work points found for the selected date range.')}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default WorkPointsTab;
